import React, { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../stores/worldStore';

export default function PanelPlacement({ browser, onPlace, onCancel }: {
  browser: boolean; onPlace: (position: [number, number, number], rotation: [number, number, number]) => void; onCancel: () => void;
}) {
  const { camera, gl } = useThree();
  const ghost = useRef<THREE.Group>(null);
  const callbacks = useRef({ onPlace, onCancel });
  callbacks.current = { onPlace, onCancel };
  useEffect(() => {
    const g = ghost.current!;
    const canvas = gl.domElement;
    const ray = new THREE.Raycaster();
    const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const point = new THREE.Vector3();
    const forward = camera.getWorldDirection(new THREE.Vector3());
    forward.y = 0; if (forward.lengthSq() < .001) forward.set(0, 0, -1); forward.normalize();
    g.position.copy(camera.position).addScaledVector(forward, 5);
    g.position.y = browser ? 1.7 : 1.4;
    g.rotation.y = Math.atan2(-forward.x, -forward.z);
    let down: [number, number] | null = null;
    const previousCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';
    (window as any).__panelPlacing = true;
    (window as any).__buildToolActive = true;
    const move = (event: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      ray.setFromCamera(new THREE.Vector2((event.clientX - bounds.left) / bounds.width * 2 - 1, -(event.clientY - bounds.top) / bounds.height * 2 + 1), camera);
      if (!ray.ray.intersectPlane(floor, point) || point.distanceTo(camera.position) > 100) return;
      const snap = useWorldStore.getState().snapSettings;
      const step = snap.grid ? snap.gridSize : 0;
      g.position.x = step ? Math.round(point.x / step) * step : point.x;
      g.position.z = step ? Math.round(point.z / step) * step : point.z;
    };
    const pointerDown = (e: PointerEvent) => { if (e.button === 0) { down = [e.clientX, e.clientY]; e.stopImmediatePropagation(); } };
    const pointerUp = (e: PointerEvent) => {
      if (e.button !== 0 || !down) return;
      e.stopImmediatePropagation();
      const distance = Math.hypot(e.clientX - down[0], e.clientY - down[1]); down = null;
      if (distance < 6) callbacks.current.onPlace(g.position.toArray(), [0, g.rotation.y, 0]);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); callbacks.current.onCancel(); }
      if (e.key.toLowerCase() === 'r') { e.preventDefault(); e.stopImmediatePropagation(); g.rotation.y += Math.PI / 4; }
    };
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerdown', pointerDown, true);
    canvas.addEventListener('pointerup', pointerUp, true);
    window.addEventListener('keydown', key, true);
    return () => {
      canvas.style.cursor = previousCursor;
      // Keep the placement click from selecting/moving other objects on release.
      setTimeout(() => {
        (window as any).__panelPlacing = false;
        (window as any).__buildToolActive = useWorldStore.getState().buildTool !== 'none';
      }, 50);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerdown', pointerDown, true);
      canvas.removeEventListener('pointerup', pointerUp, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [camera, gl, browser]);
  return <group ref={ghost}><mesh><boxGeometry args={[4.16, browser ? 3.16 : 2.41, .12]} /><meshBasicMaterial color="#00c2b3" transparent opacity={.45} depthWrite={false} /></mesh></group>;
}
